<template>
  <v-navigation-drawer
    class="d-flex flex-column portal-sidebar"
    v-model="ui.drawer"
    :rail="ui.rail"
    expand-on-hover
    app
    width="256"
    rail-width="56"
  >
    <!-- LOGO & HEADER -->
    <v-list-item class="sidebar-header" nav>
      <template #prepend>
        <button type="button" class="sidebar-brand"
          :aria-label="'Go to ' + (access?.platformAdministrator ? 'customers' : 'home')"
          @click="$router.push(homeRoute)">
          <img src="@/assets/images/audioguide-logo.png" alt="" />
          <span v-if="!ui.rail" class="sidebar-wordmark"><strong>WayWyser</strong><small>AUDIO GUIDES</small></span>
        </button>
      </template>
    </v-list-item>
    <v-divider />

    <!-- NAV LINKS -->
    <v-list class="flex-1 overflow-y-auto" nav density="comfortable" v-model:opened="openedGroups">
      <template v-for="item in navLinks" :key="item.title">
        <v-divider v-if="item.divider" class="my-2" />
        <v-list-group v-else-if="item.children" :value="item.title" no-action append-icon="">
          <template #activator="{ props }">
            <v-list-item v-bind="props" nav density="comfortable" router link exact>
              <template #prepend>
                <v-icon>{{ item.icon }}</v-icon>
              </template>
              <v-list-item-title v-if="!ui.rail">{{ item.title }}</v-list-item-title>
            </v-list-item>
          </template>
          <v-list-item
            v-for="child in item.children"
            :key="child.title"
            :to="child.href ? undefined : child.to"
            :href="child.href"
            :target="child.external ? '_blank' : undefined"
            router
            link
            exact
            class="pl-8"
          >
            <template #prepend>
              <v-icon size="small">{{ child.icon }}</v-icon>
            </template>
            <v-list-item-title v-if="!ui.rail">{{ child.title }}</v-list-item-title>
          </v-list-item>
        </v-list-group>
        <v-list-item v-else :to="item.to" router link exact>
          <template #prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title v-if="!ui.rail">{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>

  </v-navigation-drawer>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { customerForUser } from '@/utils/customerSelection';
import api from '@/api/client';
import { useRoute } from "vue-router";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";

const ui = useUIStore();
const auth = useAuthStore();
const route = useRoute();
const access = ref(null);
watch(() => auth.user?.profile?.sub, async userId => {
  access.value = null;
  if (!userId) return;
  try { access.value = (await api.get('/customers/access')).data; }
  catch { /* Navigation remains available after a transient access failure. */ }
}, { immediate: true });
const homeRoute = computed(() => access.value?.platformAdministrator
  ? { name: 'Customers' }
  : access.value?.ownCustomerId
    ? { name: 'CustomerDetails', params: { customerId: access.value.ownCustomerId } }
    : { name: 'AdminStart' });

// Navigation links
const navLinks = computed(() => [
  ...(access.value?.platformAdministrator ? [
    { title: "Customers", to: "/", icon: "mdi-domain" },
    { title: "Subscriptions", to: "/subscriptions", icon: "mdi-credit-card-clock-outline" },
  ] : []),
  ...(!access.value?.platformAdministrator && access.value?.ownCustomerId
    ? [{ title: "Sites & tours", to: `/customers/${access.value.ownCustomerId}`, icon: "mdi-map-marker-multiple-outline" }]
    : []),
  ...(customerForUser(auth.user?.profile?.sub) ? [

  { title: "Users", icon: "mdi-account-group", to: "/users" },

  { title: "Visitor Dashboard", to: "/tour-progress", icon: "mdi-chart-timeline-variant" },

  ] : []),
]);

const openedGroups = ref([]);
watch(
  () => route.path,
  (path) => {
    const parents = navLinks.value
      .filter((i) => i.children && i.children.some((c) => c.to === path))
      .map((i) => i.title);
    openedGroups.value = [...new Set([...openedGroups.value, ...parents])];
  },
  { immediate: true },
);

</script>

<style scoped>
.sidebar-header {
  padding: 18px 10px 14px;
}
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: white;
  text-align: left;
  cursor: pointer;
}
.sidebar-brand img { width: 42px; height: 42px; object-fit: contain; border-radius: 50%; background: white; }
.sidebar-wordmark { display: flex; flex-direction: column; min-width: 0; }
.sidebar-wordmark strong { font: 700 20px/1.1 var(--ww-font-heading); white-space: nowrap; }
.sidebar-wordmark small { margin-top: 4px; font: 700 10px/1 var(--ww-font-body); letter-spacing: .12em; white-space: nowrap; }
.pl-8 {
  padding-left: 12px !important;
}
.profile-activator {
  cursor: pointer;
  padding: 12px 0;
}
.profile-menu {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.sidebar-profile {
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: #fafbfc;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.v-list-group__items .v-list-item {
  padding-inline-start: 15px !important;
}

.sidebar-profile {
  margin-left: 10px;
}

.v-field__input {
  opacity: 1;
  min-width: 150px !important;
}
</style>
